import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { pushEvent } from '@/lib/analytics'
import type { ImportType } from '@/requests/data-import'
import {
  commitImport,
  deleteImport,
  getImport,
  listImports,
  rollbackImport,
  uploadCsv,
} from '@/requests/data-import'

export function useImportJobsQuery() {
  return useQuery({
    queryKey: ['imports'],
    queryFn: listImports,
  })
}

export function useImportJobQuery(id: string) {
  return useQuery({
    queryKey: ['imports', id],
    queryFn: () => getImport(id),
    enabled: !!id,
  })
}

export function useUploadCsvMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ file, type }: { file: File; type: ImportType }) => {
      const result = await uploadCsv(file, type)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['imports'] })
    },
  })
}

export function useCommitImportMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await commitImport(id)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async (_, id) => {
      pushEvent('data_import_completed')
      await queryClient.invalidateQueries({ queryKey: ['imports'] })
      await queryClient.invalidateQueries({ queryKey: ['imports', id] })
    },
  })
}

export function useRollbackImportMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await rollbackImport(id)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async (_, id) => {
      await queryClient.invalidateQueries({ queryKey: ['imports'] })
      await queryClient.invalidateQueries({ queryKey: ['imports', id] })
    },
  })
}

export function useDeleteImportMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await deleteImport(id)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['imports'] })
    },
  })
}
